import { useCallback, useEffect, useState } from 'react';

import { exportCertificates } from '../api/exportCertificatesApiService';
import type { ExportCertificatesParams } from '../types/myCertificateTypes';

/** Formats a date to DD-MM-YY-HH-MM-SS for the exported PDF filename. */
function formatFilenameDatetime(d: Date): string {
  const pad2 = (n: number) => String(n).padStart(2, '0');
  const dd = pad2(d.getDate());
  const mm = pad2(d.getMonth() + 1);
  const yy = String(d.getFullYear()).slice(-2);
  const hh = pad2(d.getHours());
  const min = pad2(d.getMinutes());
  const ss = pad2(d.getSeconds());
  return `${dd}-${mm}-${yy}-${hh}-${min}-${ss}`;
}

/**
 * Downloads `url` in-page: fetches the PDF as a blob and saves it via a
 * temporary anchor click. Deliberately NOT `window.open(url, '_blank')` —
 * that flashes a blank tab (fullscreen on tablets) until the browser turns
 * the navigation into a download, which reads as the page going blank
 * between the "started" and "completed" snackbars.
 */
async function downloadFile(url: string, filename: string): Promise<void> {
  const response = await fetch(url);
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(objectUrl);
}

/** Return values exposed by {@link useCertificateExport}. */
export interface UseCertificateExportReturn {
  /** True while an export request is in flight. */
  isExporting: boolean;
  /** 1–3 — number of trailing dots for the animated in-progress snackbar. */
  exportDotCount: number;
  /** Success snackbar visibility + close handler. */
  exportSuccessOpen: boolean;
  closeExportSuccess: () => void;
  /** Error snackbar visibility + close handler. */
  exportErrorOpen: boolean;
  closeExportError: () => void;
  /** "Nothing to export" snackbar visibility + close handler. */
  exportEmptyOpen: boolean;
  closeExportEmpty: () => void;
  /**
   * Runs the export flow: short-circuits to the empty snackbar when
   * `itemCount` is 0 (no API call), otherwise calls the export API with
   * `params`, downloads the presigned URL in-page (blob + anchor click, so
   * the current view never blanks), and maps a 404 to the empty snackbar /
   * any other failure to the error snackbar.
   */
  handleExport: (
    params: ExportCertificatesParams,
    itemCount: number
  ) => Promise<void>;
}

/**
 * Shared certificate-list export flow — in-flight state, animated-dots
 * counter and success/error/empty snackbar state. Used by the technician
 * "Moje certyfikaty" page and the client Certyfikaty tab; each caller builds
 * its own filter params and renders its own button/snackbars.
 */
export function useCertificateExport(): UseCertificateExportReturn {
  const [isExporting, setIsExporting] = useState(false);
  const [exportDotCount, setExportDotCount] = useState(1);
  const [exportSuccessOpen, setExportSuccessOpen] = useState(false);
  const [exportErrorOpen, setExportErrorOpen] = useState(false);
  const [exportEmptyOpen, setExportEmptyOpen] = useState(false);

  // Cycles the loading snackbar's trailing dots ("." -> ".." -> "...")
  // for as long as an export is in progress — matches the Figma 3-frame
  // loading animation.
  useEffect(() => {
    if (!isExporting) {
      return;
    }
    setExportDotCount(1);
    const interval = setInterval(() => {
      setExportDotCount((prev) => (prev % 3) + 1);
    }, 500);
    return () => clearInterval(interval);
  }, [isExporting]);

  const handleExport = useCallback(
    async (params: ExportCertificatesParams, itemCount: number) => {
      if (itemCount === 0) {
        setExportEmptyOpen(true);
        return;
      }

      setIsExporting(true);
      try {
        const result = await exportCertificates(params);
        await downloadFile(
          result.download_url,
          `certificate-list-${formatFilenameDatetime(new Date())}.pdf`
        );
        setExportSuccessOpen(true);
      } catch (error) {
        const status = (error as { status?: number }).status;
        if (status === 404) {
          setExportEmptyOpen(true);
        } else {
          setExportErrorOpen(true);
        }
      } finally {
        setIsExporting(false);
      }
    },
    []
  );

  return {
    isExporting,
    exportDotCount,
    exportSuccessOpen,
    closeExportSuccess: useCallback(() => setExportSuccessOpen(false), []),
    exportErrorOpen,
    closeExportError: useCallback(() => setExportErrorOpen(false), []),
    exportEmptyOpen,
    closeExportEmpty: useCallback(() => setExportEmptyOpen(false), []),
    handleExport,
  };
}
